import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiService } from "../services/api";

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatPickupDate = (value?: string | null) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1].slice(-2)}`;
  }
  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  }
  return raw;
};

const formatPickupTime24 = (value?: string | null) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const twentyFourHour = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFourHour && !/am|pm/i.test(raw)) {
    return `${String(twentyFourHour[1]).padStart(2, "0")}:${twentyFourHour[2]}`;
  }
  const parsed = new Date(`1970-01-01 ${raw}`);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return raw;
};

const formatAmount = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatSchedule = (trip: any) => {
  if (trip?.scheduledAt) return formatDateTime(trip.scheduledAt);
  const date = formatPickupDate(trip?.pickupDate);
  const time = formatPickupTime24(trip?.pickupTime);
  if (date === "—" && time === "—") return "—";
  return [date !== "—" ? date : null, time !== "—" ? time : null].filter(Boolean).join(" ");
};

const statusBadgeClass = (status?: string) => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return "bg-amber-50 text-amber-700";
    case "upcoming":
      return "bg-blue-50 text-blue-700";
    case "ongoing":
      return "bg-orange-50 text-orange-700";
    case "delivered":
      return "bg-green-50 text-green-700";
    case "accepted":
      return "bg-green-50 text-green-700";
    case "rejected":
      return "bg-red-50 text-red-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid gap-1 border-b border-gray-100 py-3 last:border-0 sm:grid-cols-[11rem_1fr] sm:gap-4">
    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd className="text-sm text-gray-900 break-words">{value ?? "—"}</dd>
  </div>
);

const PointCard = ({ title, point }: { title: string; point?: any }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {title}
    </p>
    <p className="text-sm font-medium text-gray-900">{point?.address || "—"}</p>
    <div className="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-2">
      <span>
        Contact:{" "}
        {[point?.contactName, point?.contactNumber].filter(Boolean).join(" · ") ||
          "—"}
      </span>
      <span>Pincode: {point?.pincode || "—"}</span>
      <span>
        Location:{" "}
        {point?.latitude != null && point?.longitude != null
          ? `${point.latitude}, ${point.longitude}`
          : "—"}
      </span>
      <span>Reached: {point?.isReached ? "Yes" : "No"}</span>
    </div>
  </div>
);

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5">
    <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-700">
      {title}
    </h2>
    {children}
  </div>
);

const JDInstantTripDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);

  const loadTrip = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const response = await apiService.getJdInstantTripById(id);
      if (!response.success) {
        throw new Error(response.message || response.error || "Failed to load trip");
      }
      const payload = (response as any).data?.data || (response as any).data || response;
      setTrip(payload);
    } catch (err: any) {
      setError(err?.message || "Failed to load trip details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTrip();
  }, [loadTrip]);

  const handleAccept = async () => {
    if (!id) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await apiService.respondToJdInstantTrip(id, {
        action: "accept",
      });
      if (!response.success) {
        throw new Error(response.message || "Failed to accept trip");
      }
      await loadTrip();
    } catch (err: any) {
      setError(err?.message || "Failed to accept trip");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    const remarks = rejectRemarks.trim();
    if (!remarks) {
      setError("Remarks are required when rejecting a trip");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await apiService.respondToJdInstantTrip(id, {
        action: "reject",
        remarks,
      });
      if (!response.success) {
        throw new Error(response.message || "Failed to reject trip");
      }
      setShowRejectModal(false);
      setRejectRemarks("");
      await loadTrip();
    } catch (err: any) {
      setError(err?.message || "Failed to reject trip");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading trip details...</div>;
  }

  if (!trip) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-sm text-red-600">{error || "Trip not found"}</p>
        <Link to="/jd-instant" className="text-sm font-medium text-jd-green">
          Back to JD Instant
        </Link>
      </div>
    );
  }

  const vendorStatus = trip.vendorApproval?.status || "";
  const pkg = trip.package || {};

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => navigate("/jd-instant")}
            className="text-sm font-medium text-jd-green hover:underline"
          >
            ← Back to JD Instant
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">
              {trip.tripId || "Trip details"}
            </h1>
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${statusBadgeClass(trip.status)}`}
            >
              {trip.status}
            </span>
            {vendorStatus ? (
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${statusBadgeClass(vendorStatus)}`}
              >
                Vendor: {vendorStatus}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-gray-500">
            Created {formatDateTime(trip.createdAt)} · Scheduled {formatSchedule(trip)}
          </p>
        </div>

        {vendorStatus === "pending" ? (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={handleAccept}
              className="rounded-lg bg-jd-green px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              Accept trip
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => {
                setShowRejectModal(true);
                setRejectRemarks("");
                setError("");
              }}
              className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              Reject trip
            </button>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {vendorStatus === "rejected" && trip.vendorApproval?.remarks ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Rejected remarks: {trip.vendorApproval.remarks}
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard title="Trip Summary">
          <dl>
            <InfoRow label="Trip ID" value={trip.tripId} />
            <InfoRow label="Trip status" value={trip.status} />
            <InfoRow label="Pickup date" value={formatPickupDate(trip.pickupDate)} />
            <InfoRow label="Pickup time" value={formatPickupTime24(trip.pickupTime)} />
            <InfoRow label="Scheduled at" value={formatDateTime(trip.scheduledAt)} />
            <InfoRow label="Return to pickup" value={trip.isReturnedOnPickup ? "Yes" : "No"} />
            <InfoRow label="Trip started" value={trip.tripStarted ? "Yes" : "No"} />
            <InfoRow label="Started at" value={formatDateTime(trip.startedAt)} />
            <InfoRow label="Completed at" value={formatDateTime(trip.completedAt)} />
          </dl>
        </SectionCard>

        <SectionCard title="Package & Vehicle">
          <dl>
            <InfoRow
              label="Package"
              value={[pkg.vehicleName, pkg.label].filter(Boolean).join(" · ") || "—"}
            />
            <InfoRow label="Category" value={pkg.vehicleCategory || trip.vehicleCategory || "—"} />
            <InfoRow label="Included KM" value={pkg.includedKm ?? "—"} />
            <InfoRow label="Included hours" value={pkg.includedHours ?? "—"} />
            <InfoRow label="Included drops" value={pkg.includedDrops ?? "—"} />
            <InfoRow label="Capacity (kg)" value={pkg.capacityKg ?? "—"} />
            <InfoRow label="Body type" value={pkg.bodyType || "—"} />
          </dl>
        </SectionCard>

        <SectionCard title="Driver & Assignment">
          <dl>
            <InfoRow label="Driver name" value={trip.driverDetails?.name} />
            <InfoRow label="Driver mobile" value={trip.driverDetails?.mobileNumber} />
            <InfoRow label="Vehicle number" value={trip.driverDetails?.vehicleNumber} />
            <InfoRow
              label="Assigned at"
              value={formatDateTime(trip.assignmentDetails?.assignedAt)}
            />
            <InfoRow label="Cost To Vendor (E)" value={formatAmount(trip.costToVendor)} />
            <InfoRow label="Vendor status" value={vendorStatus || "—"} />
            <InfoRow
              label="Vendor responded at"
              value={formatDateTime(trip.vendorApproval?.respondedAt)}
            />
          </dl>
        </SectionCard>

        <SectionCard title="Goods">
          {trip.goods?.length ? (
            <ul className="space-y-2">
              {trip.goods.map((good: any) => (
                <li
                  key={good._id || good.name}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
                >
                  {[good.name, good.category].filter(Boolean).join(" · ")}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No goods selected for this trip.</p>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Route">
        <div className="space-y-3">
          <PointCard title="Pickup" point={trip.startPoint} />
          {(trip.checkpoints || []).map((checkpoint: any, index: number) => (
            <PointCard
              key={checkpoint._id || index}
              title={`Checkpoint ${checkpoint.sequence || index + 1}${
                checkpoint.status ? ` (${checkpoint.status})` : ""
              }`}
              point={checkpoint.point}
            />
          ))}
          <PointCard title="Drop" point={trip.endPoint} />
        </div>
      </SectionCard>

      {showRejectModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <h2 className="text-lg font-semibold text-gray-900">Reject trip</h2>
            <p className="mt-1 text-sm text-gray-500">
              Please share why you are rejecting this trip.
            </p>
            <textarea
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              rows={4}
              className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Enter remarks"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleReject}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                Reject trip
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default JDInstantTripDetail;
